import { useCallback, useEffect, useMemo, useState } from 'react';

import { OptionType } from '@evinced-private/ui-common';

import {
	ConfiguredValues,
	markOptionsWithWarningsFromKeyValue
} from 'src/common/helpers/ConfigurationWarningsHelper';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { extractLabelKey, parseLabel, TestRowSharedType } from '../helpers';
import { extractExistingLabels } from '../remove-label-tests-modal/helpers/RemoveLabelTestsModalHelpers';
import { AppType, isMobileAppType } from '../shared-modal/constants';
import type { LabelRemoval } from '../shared-modal/LabelManagementSharedModal.types';

// Native ClickHouse column label prefixes for MFA/MSDK — ingestion-set, not removable via custom_fields
const NATIVE_MOBILE_LABEL_PREFIXES_LC = [
	'appname:',
	'appid:',
	'appversion:',
	'devicemanufactured:',
	'devicemodel:',
	'devicetype:',
	'ostype:',
	'osversion:',
	'appbuildnumber:',
	'productversion:'
];

const MOBILEKIT_LABEL = 'mobilekit';

const isNativeMobileLabel = (label: string): boolean => {
	const lower = label.toLowerCase();
	if (lower === MOBILEKIT_LABEL) {
		return true;
	}
	return NATIVE_MOBILE_LABEL_PREFIXES_LC.some((prefix) => lower.startsWith(prefix));
};

interface UseLabelsRemovalProps {
	testRows: TestRowSharedType[];
	appType?: AppType;
	configuredValues: ConfiguredValues;
	tenantConfiguredLabels?: ConfigurationLabel[];
}

interface UseLabelsRemovalReturn {
	selectedLabel: OptionType[];
	setSelectedLabel: (label: OptionType[]) => void;
	removals: LabelRemoval[];
	labelOptions: OptionType[];
	removeRemoval: (index: number) => void;
	resetRemovals: () => void;
}

export const useLabelsRemoval = ({
	testRows,
	appType,
	configuredValues,
	tenantConfiguredLabels
}: UseLabelsRemovalProps): UseLabelsRemovalReturn => {
	const [selectedLabel, setSelectedLabel] = useState<OptionType[]>([]);
	const [removals, setRemovals] = useState<LabelRemoval[]>([]);
	const labelOptions = useMemo((): OptionType[] => {
		const options = extractExistingLabels(testRows, tenantConfiguredLabels);
		const filtered = isMobileAppType(appType)
			? options.filter((opt) => !isNativeMobileLabel(opt.value as string))
			: options;

		return markOptionsWithWarningsFromKeyValue(
			filtered,
			configuredValues,
			extractLabelKey,
			parseLabel
		);
	}, [testRows, configuredValues, appType, tenantConfiguredLabels]);

	// Auto-append the selected label to removals if new, then reset selection
	useEffect(() => {
		if (selectedLabel.length > 0) {
			const next = selectedLabel[0];
			setRemovals((prev) => {
				if (prev.some((r) => r.label.value === next.value)) {
					return prev;
				}
				return [...prev, { label: next }];
			});
			setSelectedLabel([]);
		}
	}, [selectedLabel]);

	const removeRemoval = useCallback((index: number): void => {
		setRemovals((prev) => prev.filter((_, i) => i !== index));
	}, []);

	const resetRemovals = useCallback((): void => {
		setRemovals([]);
		setSelectedLabel([]);
	}, []);

	return {
		selectedLabel,
		setSelectedLabel,
		removals,
		labelOptions,
		removeRemoval,
		resetRemovals
	};
};
